import moment from "moment";
import {
    discretionarySalesChargeReturnRate,
    getAdminFeeRateForPlanType,
    getDepositModeConfigValueForKeyWithState, getDepositoryFee,
    getDepositScheduleConfigValueForKeyWithState, getEAPConfigValue, getEnrolmentFee, getInterestRateForProjections,
    getNumberValueFromRESPConfig, getPlanTypesConfigValueForKey, getProvincialTaxRateConfigForKey
} from "./ConfigUtil";
import {
    beneficiaryAge,
    beneficiaryCalendarYearsAge,
    calculatePlanDepositAmount,
    PLAN_TYPE_IDS,
    planNameForPlanTypeIDUntranslated,
    round2dec, roundHalfEven,
    roundHalfUp
} from "./PlanUtils";
import {
    calculateCompletionInsuranceCostForUnits,
    calculateCriticalIllnessInsuranceCostForUnits,
    calculateNomineeInsuranceCostForUnits
} from "./InsuranceUtils";
import {calculateYearlyIncomeGross, calculateYearlyIncomeNet} from "./KYCUtils";

export const pieChartMinimumPercentForLabel = 0.03; //When a pie chart is being drawn for the quotes/summaries, this is the minimum percentage that a slice must be to show the label inside the slice.
export const initProjectionArray = () => {
    let projectionArray = Array(252);
    for (let i = 0; i < 252; i++) {
        projectionArray[i] = {
            PlanDepositAmount: 0.0,
            CPInsuranceAmount: 0.0,
            PPInsuranceAmount: 0.0,
            CIInsuranceAmount: 0.0,
            NIInsuranceAmount: 0.0,
            TotalDepositAmount: 0.0,
            TotalInsuranceAmount: 0.0,
            CLBAmount: 0.0,
            CESGAmount: 0.0,
            ACESGAmount: 0.0,
            ACESAmount: 0.0,
            QESIAmount: 0.0,
            AQESIAmount: 0.0,
            SAGESAmount: 0.0,
            BCTESGAmount: 0.0,
            AirMiles: 0.0,
            AdminFeeCharge: 0.0,
            DepositoryFeeCharge: 0.0,
            EnrolmentFeeCharge: 0.0,
            AdminFeeChargeFromPlanDeposits: 0.0,
            AdminFeeChargeFromCESGInterest: 0.0,
            AdminFeeChargeFromACESGInterest: 0.0,
            AdminFeeChargeFromCLBInterest: 0.0,
            AdminFeeChargeFromPLBInterest: 0.0,
            AdminFeeChargeFromACESInterest: 0.0,
            AdminFeeChargeFromQESIInterest: 0.0,
            AdminFeeChargeFromAQESIInterest: 0.0,
            AdminFeeChargeFromSAGESInterest: 0.0,
            AdminFeeChargeFromBCTESGInterest: 0.0,
            PlanDepositInterest: 0.0,
            CESGInterest: 0.0,
            ACESGInterest: 0.0,
            CLBInterest: 0.0,
            ACESInterest: 0.0,
            QESIInterest: 0.0,
            AQESIInterest: 0.0,
            SAGESInterest: 0.0,
            BCTESGInterest: 0.0,
            InterestDeficiency: 0.0,
        };
    }

    return projectionArray;
}
export const loadDepositValuesIntoProjectionArray = (state, quoteAttributes) =>{
    let projectionArray = initProjectionArray();

    if (!state.plan.effectiveDate) { return; }

    let projectionMonth = moment(state.plan.effectiveDate).month() + 1;
    let projectionYear = moment(state.plan.effectiveDate).year();
    const depositsPerYear = getDepositModeConfigValueForKeyWithState(state, "NumberOfDepositsPerYear");
    let totalDeposits = 0; // A count of the total number of expected deposits

    if (state.plan.planTypeID === PLAN_TYPE_IDS.GROUP) {
        totalDeposits = getDepositScheduleConfigValueForKeyWithState(state, "NumberOfAdditionalDeposits") + 1;
    } else {
        if (state.plan.depositMode === 'T') {
            totalDeposits = 1;
        } else {
            totalDeposits = depositsPerYear * state.plan.depositCode;
        }
    }

    let trackDepositCount = 0;
    const planDepositAmount = calculatePlanDepositAmount(state);
    const CPIAmount = calculateCompletionInsuranceCostForUnits(state, true);
    const NIAmount = calculateNomineeInsuranceCostForUnits(state, true);
    const CIAmount = calculateCriticalIllnessInsuranceCostForUnits(state, true);

    const totalDeposit = planDepositAmount + CPIAmount + NIAmount + CIAmount; //Amount required per deposit
    let totalDepositAmount = 0.0; //Total lifetime deposit amount
    const lifetimeLimit = getNumberValueFromRESPConfig("LifetimeContributionLimit");

    for (let i = 0; i < 252 && trackDepositCount < totalDeposits ; i++) {
        if (i === 0 || i % (12 / depositsPerYear) === 0) {
            //When we get here it is because we are in a month where there should be a deposit.
            trackDepositCount += 1;

            projectionArray[i].PlanDepositAmount = planDepositAmount;
            projectionArray[i].CPInsuranceAmount = CPIAmount;
            projectionArray[i].NIInsuranceAmount = NIAmount;
            projectionArray[i].CIInsuranceAmount = CIAmount;
            projectionArray[i].TotalDepositAmount = totalDeposit;

            projectionArray[i].projectionMonthIndex = i;

            totalDepositAmount += planDepositAmount;
            //This keeps getting pushed out until at the end it represents the last deposit date
            quoteAttributes.finalDepositDate = moment([projectionYear, projectionMonth - 1, quoteAttributes.depositDay])

            //If we have exceeded the lifetime limit, then we don't accept any more deposits.
            if (totalDepositAmount > lifetimeLimit) {
                break;
            }
        }

        if (i === 1 && totalDeposits > 1) {
            quoteAttributes.amountRequiredOnNextDeposit = totalDeposit;
        }

        //Move to the next month if we have more deposits to include
        if (trackDepositCount < totalDeposits) {
            if (projectionMonth === 12) {
                projectionMonth = 0;
                projectionYear += 1;
            }
            projectionMonth += 1;
        }
    }

    return projectionArray;
}

export const projectGrants = (state, projectionArray, quoteAttributes) => {

    if (!state.beneficiary.birthdate) { return; }
    if (!state.plan.effectiveDate) { return; }

    let applyCESG = state.application.grantCanadaEducationSavings;
    let applyQESI = false;
    let applySAGES = false;
    let applyBCTESG = false
    let paidBCTESG = false;
    let applyCLB = state.application.grantCanadaLearningBond;

    if (state.application.grantProvincial) {
        applyQESI = state.common.signingProvince === 'PQ';
        applySAGES = state.common.signingProvince === 'SK';
        applyBCTESG = state.common.signingProvince === 'BC';
    }

    let ACESGGrantPercentage = getACESGPercentBasedOnFamilyIncomeLevel(state);
    let CESGGrantPercentage = getNumberValueFromRESPConfig("CESGGrantPercentage");
    let SAGESGrantPercentage = getNumberValueFromRESPConfig("SAGESGrantPercentage");

    let AQESIGrantPercentage = getAQESIPercentBasedOnFamilyIncomeLevel(state);
    let QESIGrantPercentage = getNumberValueFromRESPConfig("QESIGrantPercentage");

    let BCTESGGrantAmount = getNumberValueFromRESPConfig("BCTESGGrantAmount");

    if (!applyCESG) {
        CESGGrantPercentage = 0.0;
        ACESGGrantPercentage = 0.0;
    }
    if (!applySAGES) {
        SAGESGrantPercentage = 0.0;
    }
    if (!applyQESI) {
        QESIGrantPercentage = 0.0;
        AQESIGrantPercentage = 0.0; //TODO: THis was actually not done in the iPad - I think it was a bug
    }

    //BCTESG only for benes born after 2006?
    //TODO: Confirm this rule
    if (moment(state.beneficiary.birthdate).year() < 2007) {
        applyBCTESG = false;
    }
    if (!applyBCTESG) {
        BCTESGGrantAmount = 0.0;
    }

    const CLBIncomeThreshold = getNumberValueFromRESPConfig("CLBIncomeThreshold");
    if (calculateYearlyIncomeNet(state.subscriber, state.jointSubscriber, state.knowYourClient) > CLBIncomeThreshold) {
        applyCLB = false;
    }
    if (moment(state.beneficiary.birthdate).year() < 2004) {
        applyCLB = false;
    }

    const CESGLifetimeLimit = getNumberValueFromRESPConfig("CESGLifetimeGrantLimit");
    const SAGESLifetimeLimit = getNumberValueFromRESPConfig("SAGESLifetimeGrantLimit");
    const QESILifetimeLimit = getNumberValueFromRESPConfig("QESILifetimeGrantLimit");

    const beneBirthYear = moment(state.beneficiary.birthdate).year();
    const effectiveYear = moment(state.plan.effectiveDate).year();
    const effectiveMonth = moment(state.plan.effectiveDate).month() + 1;
    const beneCalendarAge = effectiveYear - beneBirthYear;
    let trackBeneAge = beneCalendarAge;
    let monthsBackdated = moment().diff(moment(state.plan.effectiveDate), 'months');
    if (monthsBackdated < 0) {
        monthsBackdated = 0;
    }

    let grantRoom = 500.0 + state.plan.grantRoom;

    //Maxmimum CESG grant that can be received in a year including grant room.
    const maxYearlyCESGGrantAmount = getNumberValueFromRESPConfig("MaxYearlyCESGGrant");
    let currentYearMaxCESGGrantAmount = grantRoom;   //Maximum CESG that can be received in the current year - used ot track how much grant room is remaining going month to month

    //Maximum CESG each year is $1000, or the total grant room that is available, whichever is less.
    if (currentYearMaxCESGGrantAmount > maxYearlyCESGGrantAmount) {
        currentYearMaxCESGGrantAmount = maxYearlyCESGGrantAmount;
    }

    //maximum yearlyACESG grant that can be received based on ACESG percentage, and maximum amount that can receive ACESG in a year.
    const maxYearlyACESGGrantAmount = getNumberValueFromRESPConfig("MaxYearlyContributionForACESGGrant") * getACESGPercentBasedOnFamilyIncomeLevel(state);

    //Maxmimum CESG grant that can be received in a year including grant room.
    const maxYearlySAGESGrantAmount = getNumberValueFromRESPConfig("MaxYearlySAGESGrant");

    let currentYearCESGTotal = 0.0;      //Tracks the total CESG received in the current year of projections
    let currentYearACESGTotal = 0.0;     //Tracks the total ACESG received in the current year of projections
    let currentYearSAGESTotal = 0.0;     //Tracks the total SAGES received in the current year of projections

    let totalCESG = 0.0;
    let totalACESG = 0.0;
    let totalSAGES = 0.0;
    let totalCLB = 0.0;

    const maxYearlyQESIGrantAmount = getNumberValueFromRESPConfig("MaxYearlyQESIGrant");       //Maxmimum CESG grant that can be received in a year including grant room.
    let currentYearMaxQESIGrantAmount = grantRoom;                   //Maximum CESG that can be received in the current year - used ot track how much grant room is remaining going month to month
    const maxYearlyAQESIGrantAmount = getNumberValueFromRESPConfig("MaxYearlyContributionForAQESIGrant") * getAQESIPercentBasedOnFamilyIncomeLevel(state);          //maximum yearlyACESG grant that can be received based on ACESG percentage, and maximum amount that can receive ACESG in a
    let currentYearQESITotal = 0.0;      //Tracks the total QESI received in the current year of projections
    let currentYearAQESITotal = 0.0;     //Trakcs the total AQESI received in the current year of projections
    let totalQESI = 0.0;
    let totalAQESI = 0.0;

    let accumulateBackDatedAmount = 0.0; //For back-dated plans, the total deposits that will be rolled into the initial deposit

    //ACES is gone
    /*let initialACESAmount = getNumberValueFromRESPConfig("ACESInitialAmount");
    const year8ACESAmount = getNumberValueFromRESPConfig("ACESOngoingAmount");
    const year11ACESAmount = getNumberValueFromRESPConfig("ACESOngoingAmount");
    const year14ACESAmount = getNumberValueFromRESPConfig("ACESOngoingAmount");

    if (beneficiaryCalendarYearsAge(state.beneficiary.birthdate) >= 7 || moment(state.beneficiary.birthdate).year() < 2005) {
        initialACESAmount = 0.0;
    }
     */

    const initialCLBAmount = getNumberValueFromRESPConfig("CLBInitialAmount");
    const yearlyCLBAmount = getNumberValueFromRESPConfig("CLBYearlyAmount");

    //Used to track the current month of the projection process.
    let projectionMonth = moment(state.plan.effectiveDate).month() + 1;
    //Year of the effective date is the year when projections start.
    let projectionYear = moment(state.plan.effectiveDate).year();


    let yearlyMaxCESGReached = false;         //Set to YES when the maximum CESG grant is reached in a 12 month period.  Reset to NO after every 12 months.
    let lifetimeMaxCESGReached = false;       //Set to YES when the lifetime CESG limit is reached.
    let yearlyMaxACESGReached = false;        //Set to YES when the maximum ACESG grant is reached in a 12 month period.  Reset to NO after every 12 months.
    let yearlyMaxSAGESReached = false;         //Set to YES when the maximum CESG grant is reached in a 12 month period.  Reset to NO after every 12 months.
    let lifetimeMaxSAGESReached = false;       //Set to YES when the lifetime CESG limit is reached.

    let yearlyMaxQESIReached = false;         //Set to YES when the maximum CESG grant is reached in a 12 month period.  Reset to NO after every 12 months.
    let lifetimeMaxQESIReached = false;       //Set to YES when the lifetime CESG limit is reached.
    let yearlyMaxAQESIReached = false;        //Set to YES when the maximum ACESG grant is reached in a 12 month period.  Reset to NO after every 12 months.

    let trackNumOfDeposits = 0; //Tracks the number of deposits during the projetions.
    let numberOfAdditionalDeposits = 0;

    if (state.plan.planTypeID === PLAN_TYPE_IDS.GROUP) {
        numberOfAdditionalDeposits = getDepositScheduleConfigValueForKeyWithState(state, "NumberOfAdditionalDeposits");
    } else {
        numberOfAdditionalDeposits = getDepositModeConfigValueForKeyWithState(state, "NumberOfDepositsPerYear");
        numberOfAdditionalDeposits *= state.plan.depositCode;
    }

    //TODO: It looks like this used to be 17 or maybe still should be?
    if (trackBeneAge > 21) {
        return;
    }

    for (let i = 0; i < 252; i++) {

        // CLB
        if (applyCLB && (i % 12 === monthsBackdated || i === monthsBackdated) && totalCLB<2000 && i >= monthsBackdated) {
            let amount = 0.0;
            if (i === monthsBackdated) {
                //In the first year, they get $500 plus $100 for each previous year the family was eligible for the NCB
                amount = initialCLBAmount + (yearlyCLBAmount * trackBeneAge);
            } else {
                amount = yearlyCLBAmount;
            }
            if (amount > 2000){//Superpad Fix over 16 year old CLB plans
                amount = 2000;
            }
            totalCLB += amount;

            projectionArray[i].CLBAmount = amount;
        }


        const currentMonthPlanDepositAmount = projectionArray[i].PlanDepositAmount;
        if (currentMonthPlanDepositAmount > 0) {
            trackNumOfDeposits += 1;
        }

        if (i < monthsBackdated) {
            accumulateBackDatedAmount += currentMonthPlanDepositAmount;
        }


        if ((currentMonthPlanDepositAmount > 0 || accumulateBackDatedAmount > 0) && trackNumOfDeposits <= (numberOfAdditionalDeposits + 1) && i >= monthsBackdated) {

            // CESG
            if (!yearlyMaxCESGReached && !lifetimeMaxCESGReached) {
                let grant = roundHalfUp((currentMonthPlanDepositAmount + accumulateBackDatedAmount) * CESGGrantPercentage);

                //If the new CESG grant will put them over the yearly limit for CESG + Grant Room (not including ACESG grant room)
                if (currentYearCESGTotal + grant > currentYearMaxCESGGrantAmount) {
                    //If we're over, then we set the current month grant to the amount that brings us to the max.
                    grant = currentYearMaxCESGGrantAmount - currentYearCESGTotal;
                    yearlyMaxCESGReached = true;
                }

                //If the new CESG grant will put them over the lifetime CESG limit
                if (totalCESG + totalACESG + grant > CESGLifetimeLimit) {
                    //set the current month grant to the amount that brings them to the lifetime limit
                    grant = CESGLifetimeLimit - totalCESG - totalACESG;
                    lifetimeMaxCESGReached = true;
                }

                projectionArray[i].CESGAmount = grant;
                currentYearCESGTotal += grant;
                totalCESG += grant;
                grantRoom -= grant;
            }

            // ACESG
            if (!yearlyMaxACESGReached && !lifetimeMaxCESGReached) {
                let grant = roundHalfUp((currentMonthPlanDepositAmount + accumulateBackDatedAmount) * ACESGGrantPercentage);


                //If the new ACESG grant will put them over the yearly limit for ACESG
                if (currentYearACESGTotal + grant > maxYearlyACESGGrantAmount) {

                    //If we're over, then we set the current month grant to the amount that brings us to the max.
                    grant = maxYearlyACESGGrantAmount - currentYearACESGTotal;
                    yearlyMaxACESGReached = true; //This one checks for ACESG lifetime limit
                }

                //If the new ACESG grant will put them over the lifetime CESG limit
                if (totalCESG + totalACESG + grant > CESGLifetimeLimit) {

                    //set the current month grant to the amount that brings them to the lifetime limit
                    grant = CESGLifetimeLimit - totalCESG - totalACESG;
                    lifetimeMaxCESGReached = true; //This one checks for CESG lifetime limit
                    //break; //Break out of the loop if lifetime limit is reached.
                }

                projectionArray[i].ACESGAmount = grant;
                currentYearACESGTotal += grant;
                totalACESG += grant;
            }


            // QESI
            if (applyQESI) {
                if (state.quote.quoteBasedOn === 'units') {
                    if (!yearlyMaxQESIReached && !lifetimeMaxQESIReached) {
                        // We just assume QESI is going to be half the CESG amount.
                        //TODO: There is no rounding done here which is the same as the iPad... is that correct?
                        const cesgHack = projectionArray[i].CESGAmount / 2;
                        projectionArray[i].QESIAmount = cesgHack;
                        currentYearQESITotal += cesgHack;
                        totalQESI += cesgHack;
                    }
                } else {
                    //Quote based on deposit amount
                    if (!yearlyMaxQESIReached && !lifetimeMaxQESIReached) {
                        let grant = roundHalfUp((currentMonthPlanDepositAmount + accumulateBackDatedAmount) * QESIGrantPercentage);
                        if (i === 0) {
                            //TODO: This is hardcoded in the iPad, but it should probably come from the RESP key MaxYearlyQESIGrant
                            currentYearMaxQESIGrantAmount = 250
                        }

                        if ((currentYearQESITotal + grant) > (currentYearMaxQESIGrantAmount + grantRoom)) {
                            //If we're over, then we set the current month grant to the amount that brings us to the max.
                            if (state.plan.depositMode === 'P') {
                                //This is some kind of hack for 5 year monthly plans
                                grant = currentYearQESITotal - currentYearMaxQESIGrantAmount;
                                if (grant < 0) {
                                    grant = 0.0;
                                    if (state.plan.numberOfUnits === 20.0 && (i === 48 || i === 36 || i === 24)) {
                                        // Ridiculous hack for 5 year monthly with 20 units??
                                        //TODO: What is this??
                                        grant = roundHalfUp(((currentMonthPlanDepositAmount + accumulateBackDatedAmount) * QESIGrantPercentage) * 0.58);
                                    }
                                }
                            } else {
                                if (grant >= currentYearMaxQESIGrantAmount - currentYearQESITotal) {
                                    //set the current month grant to the amount that brings them to the yearly limit
                                    grant = currentYearMaxQESIGrantAmount - currentYearQESITotal;
                                }
                                if (state.plan.depositMode === 'X' && state.plan.numberOfUnits === 20.0 && i === 52) {
                                    // Ridiculous hack for 10 year monthly with 20 units
                                    //TODO: What is this??
                                    grant = roundHalfUp((currentMonthPlanDepositAmount + accumulateBackDatedAmount) * QESIGrantPercentage);
                                }
                            }

                            yearlyMaxQESIReached = true;
                        }

                        //If the new QESI grant will put them over the lifetime QESI limit
                        if (totalQESI + totalAQESI + grant > QESILifetimeLimit) {
                            //set the current month grant to the amount that brings them to the lifetime limit
                            grant = QESILifetimeLimit - totalQESI - totalAQESI;
                            lifetimeMaxQESIReached = true;
                        }

                        if (grant > totalCESG / 2) {
                            grant = totalCESG / 2;
                        }
                        projectionArray[i].QESIAmount = grant;

                        currentYearQESITotal += grant;
                        totalQESI += grant;
                    }
                }
            }

            // AQESI
            if (!yearlyMaxQESIReached && !lifetimeMaxQESIReached) {
                let grant = roundHalfUp((currentMonthPlanDepositAmount + accumulateBackDatedAmount) * AQESIGrantPercentage);

                //If the new AQESI grant will put them over the yearly limit for AQESI
                if (currentYearAQESITotal + grant > maxYearlyAQESIGrantAmount) {
                    //If we're over, then we set the current month grant to the amount that brings us to the max.
                    grant = maxYearlyAQESIGrantAmount - currentYearAQESITotal;
                    yearlyMaxAQESIReached = true;
                }


                //If the new AQESI grant will put them over the lifetime QESI limit
                if (totalQESI + totalAQESI + grant > QESILifetimeLimit) {
                    //set the current month grant to the amount that brings them to the lifetime limit
                    grant = QESILifetimeLimit - totalQESI - totalAQESI;
                    lifetimeMaxQESIReached = true;
                }

                projectionArray[i].AQESIAmount = grant;
                currentYearAQESITotal += grant;
                totalAQESI += grant;
            } else {
                projectionArray[i].AQESIAmount = 0.0;
            }

            accumulateBackDatedAmount = 0.0;
        } else {
            projectionArray[i].CESGAmount = 0.0;
            projectionArray[i].ACESGAmount = 0.0;
            projectionArray[i].QESIAmount = 0.0;
            projectionArray[i].AQESIAmount = 0.0;
        }

        if (applyBCTESG && !paidBCTESG && (trackBeneAge >= 6 && trackBeneAge <= 9) &&
            ((projectionYear > 2015) || (projectionYear=2015 && projectionMonth>=8))) {
            projectionArray[i].BCTESGAmount = BCTESGGrantAmount;
            paidBCTESG = true;
        } else {
            projectionArray[i].BCTESGAmount = 0.0;
        }


        if (projectionMonth === 12) {
            //Every 12 months, reset the following

            projectionYear++;
            projectionMonth = 0; //Gets set to 1 below.
            yearlyMaxCESGReached = false;
            yearlyMaxACESGReached = false;
            yearlyMaxSAGESReached = false;
            yearlyMaxQESIReached = false;
            yearlyMaxAQESIReached = false;

            grantRoom += 500;
            currentYearMaxCESGGrantAmount = grantRoom;
            if (currentYearMaxCESGGrantAmount > maxYearlyCESGGrantAmount) {
                currentYearMaxCESGGrantAmount = maxYearlyCESGGrantAmount;
            }
            if (currentYearMaxQESIGrantAmount > maxYearlyQESIGrantAmount) {
                currentYearMaxQESIGrantAmount = maxYearlyQESIGrantAmount;
            }

            currentYearCESGTotal = 0.0;
            currentYearACESGTotal = 0.0;
            currentYearSAGESTotal = 0.0;
            currentYearQESITotal = 0.0;
            currentYearAQESITotal = 0.0;

            trackBeneAge += 1;

            if (trackBeneAge > 17) {
                break;
            }
        }

        projectionMonth += 1;


    }
}



export const getACESGPercentBasedOnFamilyIncomeLevel = (state) => {
    const currentIncomeLevel = calculateYearlyIncomeGross(state.subscriber, state.jointSubscriber, state.knowYourClient);
    const incomeLevel1Max = getNumberValueFromRESPConfig("ACESGFamilyIncomeLevel1");
    const incomeLevel2Max = getNumberValueFromRESPConfig("ACESGFamilyIncomeLevel2");

    if (currentIncomeLevel < incomeLevel1Max) {
        return getNumberValueFromRESPConfig("ACESGFamilyIncomeLevel1Rate");
    } else if (currentIncomeLevel < incomeLevel2Max) {
        return getNumberValueFromRESPConfig("ACESGFamilyIncomeLevel2Rate");
    } else {
        return 0.0;
    }
}

export const getAQESIPercentBasedOnFamilyIncomeLevel = (state) => {
    const currentIncomeLevel = calculateYearlyIncomeGross(state.subscriber, state.jointSubscriber, state.knowYourClient);
    const incomeLevel1Max = getNumberValueFromRESPConfig("ACESGQuebecFamilyIncomeLevel1");
    const incomeLevel2Max = getNumberValueFromRESPConfig("ACESGQuebecFamilyIncomeLevel2");

    if (currentIncomeLevel < incomeLevel1Max) {
        return getNumberValueFromRESPConfig("ACESGQuebecFamilyIncomeLevel1Rate");
    } else if (currentIncomeLevel < incomeLevel2Max) {
        return getNumberValueFromRESPConfig("ACESGQuebecFamilyIncomeLevel2Rate");
    } else {
        return 0.0;
    }
}


export const projectIncomeAndFees = (state, projectionArray, quoteAttributes) => {
    //Rounds half up

    const GSTRate = 1 + getProvincialTaxRateConfigForKey(state.common.signingProvince, "GST");
    let adminFeeRate = getAdminFeeRateForPlanType(state.plan.planTypeID);
    let annualDepositoryFee = round2dec(getDepositoryFee(state) * GSTRate);
    annualDepositoryFee = roundHalfUp(annualDepositoryFee);
    const totalEnrolmentFee = getTotalEnrolmentFee(state);
    const halfEnrolmentFee = round2dec(totalEnrolmentFee / 2.0);


    let totalAdminFeeCharged = 0.0; //Running total of admin fee collected
    let totalDepositoryFeeCharged = 0.0; //Running total of depository fee charged
    let totalEnrolmentFeeCharged = 0.0; //Running total of enrolment fee.
    let interestRate = getInterestRateForProjections();

    //New admin fee collection method
    interestRate -= (adminFeeRate * GSTRate);
    adminFeeRate = 0.0;


    let accumulatedAmount = 0.0; //The running total of monies that we are collecting the fee from.  Goes up with deposits, and down with charges.
    let CESGAmount = 0.0;            //These variables track amounts through the projection months
    let ACESGAmount = 0.0;
    let CESGInterest = 0.0;
    let ACESGInterest = 0.0;
    let CLBAmount = 0.0;
    let CLBInterest = 0.0;
    let ACESAmount = 0.0;
    let ACESInterest = 0.0;
    let QESIAmount = 0.0;
    let AQESIAmount = 0.0;
    let AQESIInterest = 0.0;
    let QESIInterest = 0.0;
    let SAGESAmount = 0.0;
    let SAGESInterest = 0.0;

    let BCTESGAmount = 0.0;
    let BCTESGInterest = 0.0;


    let planDepositAmount = 0.0;
    let planDepositInterest = 0.0;
    let outstandingDepositoryFee = 0.0;
    let outstandingAdminFee = 0.0;
    let firstDepositAmount = 0.0;
    let firstInsuranceAmount = 0.0;
    let interestDeficiency = 0.0;
    let interestDeficiencyRate = getNumberValueFromRESPConfig("InterestDeficiencyRate") / 12.0;
    let accumulateBackDatedAmount = 0.0;


    //New business Air Miles - needs to change for other plans?
    let accumulatedDepositsForAirMiles = 0.0;
    let newBusinessAirMiles = Math.trunc(10 * state.plan.numberOfUnits); // in the iPad this is casted to an int which should be the same as truncate

    let calculatedInterestDeficiency = true;
    if (state.plan.planTypeID !== PLAN_TYPE_IDS.GROUP) {
        calculatedInterestDeficiency = false;
    }


    //This is the current month as of the date of running the projections
    let currentMonth = moment().month() + 1;
    let currentYear = moment().year();
    //This is the month and year when income and fee projections start


    //I don't see how this can happen, but a check is done here in the iPad
    if (!state.plan.effectiveDate || !state.plan.maturityDate) { return; }

    let projectionMonth = moment(state.plan.effectiveDate).month() + 1;
    let projectionYear = moment(state.plan.effectiveDate).year();
    //And the month and year when they end (maturity date)
    let stopMonth = moment(state.plan.maturityDate).month() + 1;
    let stopYear = moment(state.plan.maturityDate).year();

    let currentCharge = 0.0;

    let i = 0;
    //Loop through all possible months of the plan.  Add up all deposit types and calcualte monthly interest and admin fee
    //Also calculate depository fee charges when applicable
    for (i = 0; i < 252; i++) {
        let calculatedAmount = 0.0;
        let currentMonthPrincipalAmount = 0.0;
        currentCharge = 0.0;
        currentMonthPrincipalAmount = projectionArray[i].PlanDepositAmount;
        let currentMonthInsuranceAmount = 0.0;
        currentMonthInsuranceAmount += projectionArray[i].CPInsuranceAmount;
        currentMonthInsuranceAmount += projectionArray[i].NIInsuranceAmount;
        currentMonthInsuranceAmount += projectionArray[i].PPInsuranceAmount;
        currentMonthInsuranceAmount += projectionArray[i].CIInsuranceAmount;

        accumulatedDepositsForAirMiles += currentMonthPrincipalAmount; //Used to track when $100 increments are reached


        //If the accumulated amount for AIRMILES is greater than 100, or we are in the first month and therefore allocating new business air miles
        if (accumulatedDepositsForAirMiles >= 100.0 || newBusinessAirMiles !== 0) {
            //TODO: I believe roundHalfUp is correct here.  In the iPad it uses the builtin C function "round".  I think this is the same as roundHalfUp for positive numbers.
            calculatedAmount = roundHalfUp(accumulatedDepositsForAirMiles / 100.0, 0); //Number of AIRMILES is 1 for each $100
            accumulatedDepositsForAirMiles -= calculatedAmount * 100.0; //Reduce accumulated amount for the dollars that AIRMILES have been awarded
            calculatedAmount += newBusinessAirMiles; //Add in the new business air miles if necessary
            newBusinessAirMiles = 0;

            projectionArray[i].AirMiles = calculatedAmount; //Track AIRMILES being awarded in the current month
        }



        //We go in here if we are     looking at the first deposit, or deposits before the current month (back dating)
        if (projectionYear * 100 + projectionMonth <= currentYear * 100 + currentMonth || i === 0) {
            firstDepositAmount += currentMonthPrincipalAmount;

            firstInsuranceAmount += projectionArray[i].CPInsuranceAmount;
            firstInsuranceAmount += projectionArray[i].NIInsuranceAmount;
            firstInsuranceAmount += projectionArray[i].PPInsuranceAmount;
            firstInsuranceAmount += projectionArray[i].CIInsuranceAmount;
        }


        // ENROLMENT FEE
        //Enrolment fee is deducted at 100% until it is collected half way.
        //Then it is collected at 50% until it is fully collected.
        //Gets a bit complicated when a single deposit covers a portion of both categories.
        if (totalEnrolmentFeeCharged < halfEnrolmentFee) {
            if ((currentMonthPrincipalAmount + totalEnrolmentFeeCharged) < halfEnrolmentFee) {

                //Here we collect 100% of the deposit toward enrolment fee
                currentCharge += currentMonthPrincipalAmount;
                totalEnrolmentFeeCharged += currentMonthPrincipalAmount;
                currentMonthPrincipalAmount = 0;

            } else {
                //Here we take 100% of the deposit amount needed to bring the total enrolment fee collected to 50%.
                calculatedAmount = (halfEnrolmentFee - totalEnrolmentFeeCharged);
                currentCharge += calculatedAmount;
                totalEnrolmentFeeCharged += calculatedAmount;

                currentMonthPrincipalAmount -= calculatedAmount;

                if (totalEnrolmentFeeCharged + round2dec(currentMonthPrincipalAmount / 2.0) > totalEnrolmentFee) {

                    //Here we take as much of the deposit as we need to cover the remaining enrolment fee outstanding
                    calculatedAmount = (totalEnrolmentFee - totalEnrolmentFeeCharged);
                    currentCharge += calculatedAmount;
                    totalEnrolmentFeeCharged += calculatedAmount;

                    currentMonthPrincipalAmount -= calculatedAmount;

                } else {
                    //Here we take 50% of the deposit towards enrolment fee
                    calculatedAmount = round2dec(currentMonthPrincipalAmount / 2.0);
                    currentCharge += calculatedAmount;
                    totalEnrolmentFeeCharged += calculatedAmount;

                    currentMonthPrincipalAmount -= calculatedAmount;
                }
            }
        } else if ((currentMonthPrincipalAmount / 2.0) + totalEnrolmentFeeCharged > totalEnrolmentFee) {
            //Here we take as much deposit as we need to completely collect enrolment fee
            calculatedAmount = (totalEnrolmentFee - totalEnrolmentFeeCharged);
            currentCharge += calculatedAmount;
            totalEnrolmentFeeCharged += calculatedAmount;

            currentMonthPrincipalAmount -= calculatedAmount;
        } else {
            //And here we take 50% of the deposit towards enrolment fee
            calculatedAmount = round2dec(currentMonthPrincipalAmount / 2.0);
            currentCharge += calculatedAmount;
            totalEnrolmentFeeCharged += calculatedAmount;
            currentMonthPrincipalAmount -= calculatedAmount;
        }

        projectionArray[i].EnrolmentFeeCharge = currentCharge;




        // INTEREST DEFICIENCY
        //Handle interest deficiency here for back dated plans
        if (projectionYear * 100 + projectionMonth < currentYear * 100 + currentMonth) {
            accumulateBackDatedAmount += currentMonthPrincipalAmount;

            if (calculatedInterestDeficiency) {
                calculatedAmount = round2dec((firstDepositAmount + interestDeficiency - totalEnrolmentFeeCharged) * interestDeficiencyRate);
                interestDeficiency += calculatedAmount;

                projectionArray[i].InterestDeficiency = calculatedAmount;
            }
        }



        // NEW MONEY AND INTEREST
        //Don't calculate new money, grants, or interest if we are before the current month (i.e. due to back-dating)
        if (projectionYear * 100 + projectionMonth >= currentYear * 100 + currentMonth) {
            //NEW MONEY AND INTEREST ------------------------------------------------------------------------------------------------

            //Plan deopsit amount
            planDepositAmount += currentMonthPrincipalAmount + accumulateBackDatedAmount;
            accumulatedAmount += currentMonthPrincipalAmount + accumulateBackDatedAmount;
            //Inerest on plan deposits
            calculatedAmount = round2dec((planDepositAmount + planDepositInterest) * (interestRate / 12.0)); //Apply 1/12th of the annual interest rate.
            accumulatedAmount += calculatedAmount;
            planDepositInterest += calculatedAmount;
            projectionArray[i].PlanDepositInterest = calculatedAmount;
            accumulateBackDatedAmount = 0.0;



            //CESG amount
            calculatedAmount = projectionArray[i].CESGAmount;
            CESGAmount += calculatedAmount;
            accumulatedAmount += calculatedAmount;
            //CESG Interest
            calculatedAmount = round2dec((CESGAmount + CESGInterest) * (interestRate / 12.0));
            accumulatedAmount += calculatedAmount;
            CESGInterest += calculatedAmount;
            projectionArray[i].CESGInterest = calculatedAmount;



            //ACESG Amount
            calculatedAmount = projectionArray[i].ACESGAmount;
            ACESGAmount += calculatedAmount;
            accumulatedAmount += calculatedAmount;
            //ACESG Interest
            calculatedAmount = round2dec((ACESGAmount + ACESGInterest) * (interestRate / 12.0));
            accumulatedAmount += calculatedAmount;
            ACESGInterest += calculatedAmount;
            projectionArray[i].ACESGInterest = calculatedAmount;




            //CLB Amount
            calculatedAmount = projectionArray[i].CLBAmount;
            CLBAmount += calculatedAmount;
            accumulatedAmount += calculatedAmount;
            //CLB Interest
            calculatedAmount = round2dec((CLBAmount + CLBInterest) * (interestRate / 12.0));
            accumulatedAmount += calculatedAmount;
            CLBInterest += calculatedAmount;
            projectionArray[i].CLBInterest = calculatedAmount;





            //QESI Amount
            calculatedAmount = projectionArray[i].QESIAmount;
            QESIAmount += calculatedAmount;
            accumulatedAmount += calculatedAmount;
            //QESI Interest
            calculatedAmount = round2dec((QESIAmount + QESIInterest) * (interestRate / 12.0));
            accumulatedAmount += calculatedAmount;
            QESIInterest += calculatedAmount;
            projectionArray[i].QESIInterest = calculatedAmount;




            //AQESI Amount
            calculatedAmount = projectionArray[i].AQESIAmount;
            AQESIAmount += calculatedAmount;
            accumulatedAmount += calculatedAmount;
            //AQESI Interest
            calculatedAmount = round2dec((AQESIAmount + AQESIInterest) * (interestRate / 12.0));
            accumulatedAmount += calculatedAmount;
            AQESIInterest += calculatedAmount;
            projectionArray[i].AQESIInterest = calculatedAmount;




            //BCTESG Amount
            calculatedAmount = projectionArray[i].BCTESGAmount;
            BCTESGAmount += calculatedAmount;
            accumulatedAmount += calculatedAmount;
            //BCTESG Interest
            calculatedAmount = round2dec((BCTESGAmount + BCTESGInterest) * (interestRate / 12.0));
            accumulatedAmount += calculatedAmount;
            BCTESGInterest += calculatedAmount;
            projectionArray[i].BCTESGInterest = calculatedAmount;

        }


        //DEPOSITORY FEE --------------------------------------------------------------------------------------------------------
        currentCharge = 0.0;

        //Change depository fee on anniversary of effective date
        let thisMonthsDepositoryFeeCharge = 0.0;
        if ((i+1) % 12 === 1) {
            thisMonthsDepositoryFeeCharge = annualDepositoryFee;
        }


        if (planDepositAmount < (thisMonthsDepositoryFeeCharge + outstandingDepositoryFee)) {
            calculatedAmount = (thisMonthsDepositoryFeeCharge + outstandingDepositoryFee) - planDepositAmount;
            outstandingDepositoryFee = calculatedAmount;
            totalDepositoryFeeCharged += planDepositAmount;
            currentCharge += planDepositAmount;
            accumulatedAmount -= planDepositAmount;
            planDepositAmount = 0;
        } else {
            calculatedAmount = thisMonthsDepositoryFeeCharge + outstandingDepositoryFee;
            outstandingDepositoryFee = 0;
            totalDepositoryFeeCharged += calculatedAmount;
            currentCharge += calculatedAmount;
            accumulatedAmount -= calculatedAmount;
            planDepositAmount -= calculatedAmount;
        }

        projectionArray[i].DepositoryFeeCharge = currentCharge;

        //END DEPOSITORY FEE ----------------------------------------------------------------------------------------------------------------------------



        //ADMIN FEE -------------------------------------------------------------------------------------------------------------------
        //Don't calculate admin fee if we are before the current month (i.e. due to back-dating)
        //TODO: Cleanup the below section - This is all old admin fee stuff that is not needed anymore because we hardcode adminFeeRate to 0 above since admin fee is handled differently now
        /*
        if (projectionYear * 100 + projectionMonth >= currentYear * 100 + currentMonth) {
            currentCharge = 0.0;
            let thisMonthsAdminFeeCharge = round2dec(round2dec(accumulatedAmount * (adminFeeRate / 12.0)) * GSTRate);


            //admin fee from plan deposits
            if (planDepositAmount < (thisMonthsAdminFeeCharge + outstandingAdminFee)) {
                calculatedAmount = (thisMonthsAdminFeeCharge + outstandingAdminFee) - planDepositAmount;
                outstandingAdminFee = calculatedAmount;
                totalAdminFeeCharged += planDepositAmount;
                accumulatedAmount -= planDepositAmount;
                currentCharge += planDepositAmount;

                projectionArray[i].AdminFeeChargeFromPlanDeposits = planDepositAmount;

                planDepositAmount = 0;

            } else {
                calculatedAmount = thisMonthsAdminFeeCharge + outstandingAdminFee;
                outstandingAdminFee = 0;
                totalAdminFeeCharged += calculatedAmount;
                planDepositAmount -= calculatedAmount;
                accumulatedAmount -= calculatedAmount;
                currentCharge += calculatedAmount;

                projectionArray[i].AdminFeeChargeFromPlanDeposits = calculatedAmount;
            }


            //admin fee from CESG Interest
            if (CESGInterest < outstandingAdminFee) {
                outstandingAdminFee -= CESGInterest;
                totalAdminFeeCharged += CESGInterest;
                accumulatedAmount -= CESGInterest;
                currentCharge += CESGInterest;

                projectionArray[i].AdminFeeChargeFromCESGInterest = CESGInterest;

                CESGInterest = 0;
            } else {
                totalAdminFeeCharged += outstandingAdminFee;
                CESGInterest -= outstandingAdminFee;
                accumulatedAmount -= outstandingAdminFee;
                currentCharge += outstandingAdminFee;

                projectionArray[i].AdminFeeChargeFromCESGInterest = outstandingAdminFee;

                outstandingAdminFee = 0;
            }



            //admin fee from ACESG Interest
            if (ACESGInterest < outstandingAdminFee) {
                outstandingAdminFee -= ACESGInterest;
                totalAdminFeeCharged += ACESGInterest;
                accumulatedAmount -= ACESGInterest;
                currentCharge += ACESGInterest;

                projectionArray[i].AdminFeeChargeFromACESGInterest = ACESGInterest;

                ACESGInterest = 0;
            } else {
                totalAdminFeeCharged += outstandingAdminFee;
                ACESGInterest -= outstandingAdminFee;
                accumulatedAmount -= outstandingAdminFee;
                currentCharge += outstandingAdminFee;

                projectionArray[i].AdminFeeChargeFromACESGInterest = outstandingAdminFee;

                outstandingAdminFee = 0;
            }



            //admin fee from CLB Interest
            if (CLBInterest < outstandingAdminFee) {
                outstandingAdminFee -= CLBInterest;
                totalAdminFeeCharged += CLBInterest;
                accumulatedAmount -= CLBInterest;
                currentCharge += CLBInterest;

                projectionArray[i].AdminFeeChargeFromCLBInterest = CLBInterest;

                CLBInterest = 0;
            } else {
                totalAdminFeeCharged += outstandingAdminFee;
                CLBInterest -= outstandingAdminFee;
                accumulatedAmount -= outstandingAdminFee;
                currentCharge += outstandingAdminFee;

                projectionArray[i].AdminFeeChargeFromCLBInterest = outstandingAdminFee;

                outstandingAdminFee = 0;
            }




            //admin fee from QESI Interest
            if (QESIInterest < outstandingAdminFee) {
                outstandingAdminFee -= QESIInterest;
                totalAdminFeeCharged += QESIInterest;
                accumulatedAmount -= QESIInterest;
                currentCharge += QESIInterest;

                projectionArray[i].AdminFeeChargeFromQESIInterest = QESIInterest;

                QESIInterest = 0;
            } else {
                totalAdminFeeCharged += outstandingAdminFee;
                QESIInterest -= outstandingAdminFee;
                accumulatedAmount -= outstandingAdminFee;
                currentCharge += outstandingAdminFee;

                projectionArray[i].AdminFeeChargeFromQESIInterest = outstandingAdminFee;

                outstandingAdminFee = 0;
            }



            //admin fee from AQESI Interest
            if (AQESIInterest < outstandingAdminFee) {
                outstandingAdminFee -= AQESIInterest;
                totalAdminFeeCharged += AQESIInterest;
                accumulatedAmount -= AQESIInterest;
                currentCharge += AQESIInterest;

                projectionArray[i].AdminFeeChargeFromAQESIInterest = AQESIInterest;

                QESIInterest = 0;
            } else {
                totalAdminFeeCharged += outstandingAdminFee;
                AQESIInterest -= outstandingAdminFee;
                accumulatedAmount -= outstandingAdminFee;
                currentCharge += outstandingAdminFee;

                projectionArray[i].AdminFeeChargeFromAQESIInterest = outstandingAdminFee;

                outstandingAdminFee = 0;
            }




            //admin fee from BCTESG Interest
            if (BCTESGInterest < outstandingAdminFee) {
                outstandingAdminFee -= BCTESGInterest;
                totalAdminFeeCharged += BCTESGInterest;
                accumulatedAmount -= BCTESGInterest;
                currentCharge += BCTESGInterest;

                projectionArray[i].AdminFeeChargeFromBCTESGInterest = BCTESGInterest;

                BCTESGInterest = 0;
            } else {
                totalAdminFeeCharged += outstandingAdminFee;
                BCTESGInterest -= outstandingAdminFee;
                accumulatedAmount -= outstandingAdminFee;
                currentCharge += outstandingAdminFee;

                projectionArray[i].AdminFeeChargeFromBCTESGInterest = outstandingAdminFee;

                outstandingAdminFee = 0;
            }

            projectionArray[i].AdminFeeCharge = currentCharge;
        }
        //END ADMIN FEE ------------------------------------------------------------------------------------------------------------------------

         */

        if (projectionMonth === 12) {
            projectionMonth = 0; //Gets incremented to 1 below
            projectionYear += 1;
            accumulatedDepositsForAirMiles = 0;
        }

        projectionMonth += 1;

        //Stop at the maturity date.
        //Is this consistent with how it is actually done in real life?
        if (projectionYear >= stopYear && projectionMonth >= stopMonth) {
            break;
        }

    }


    let numberOfDepositsPerYear =  getDepositModeConfigValueForKeyWithState(state, "NumberOfDepositsPerYear");
    let totalDepositCalculation = numberOfDepositsPerYear * state.plan.depositCode;
    let addOrSubtract = 0.0;
    if (state.plan.planTypeID === PLAN_TYPE_IDS.ACHIEVERS && numberOfDepositsPerYear > 0){  //Fix issue with new Achievers Plan  superpad
       addOrSubtract = (firstDepositAmount * totalDepositCalculation) - (state.plan.numberOfUnits * 465.0);
    }

    quoteAttributes.initialDepositAmount = firstDepositAmount + firstInsuranceAmount + interestDeficiency - addOrSubtract;
}



export const getTotalEnrolmentFee = (state) => {
    //Rounds half even

    let ret = getEnrolmentFee(state);
    ret *= state.plan.numberOfUnits;
    ret = roundHalfEven(ret);

    //New Achievers
    if (state.plan.planTypeID === PLAN_TYPE_IDS.ACHIEVERS) {
        const beneAge = beneficiaryAge(state.beneficiary.birthdate);
        if (beneAge === 0)
            ret = (state.plan.numberOfUnits * 465) * 0.109;
        if (beneAge === 1)
            ret = (state.plan.numberOfUnits * 465) * 0.104;
        if (beneAge === 2)
            ret = (state.plan.numberOfUnits * 465) * 0.098;
        if (beneAge === 3)
            ret = (state.plan.numberOfUnits * 465) * 0.092;
        if (beneAge === 4)
            ret = (state.plan.numberOfUnits * 465) * 0.086;
        if (beneAge === 5)
            ret = (state.plan.numberOfUnits * 465) * 0.08;
        if (beneAge === 6)
            ret = (state.plan.numberOfUnits * 465) * 0.074;
        if (beneAge === 7)
            ret = (state.plan.numberOfUnits * 465) * 0.068;
        if (beneAge === 8)
            ret = (state.plan.numberOfUnits * 465) * 0.062;
        if (beneAge === 9)
            ret = (state.plan.numberOfUnits * 465) * 0.056;
        if (beneAge === 10)
            ret = (state.plan.numberOfUnits * 465) * 0.05;
        if (beneAge === 11)
            ret = (state.plan.numberOfUnits * 465) * 0.044;
        if (beneAge === 12)
            ret = (state.plan.numberOfUnits * 465) * 0.038;
        if (beneAge === 13)
            ret = (state.plan.numberOfUnits * 465) * 0.034;
        if (beneAge === 14)
            ret = (state.plan.numberOfUnits * 465) * 0.03;
        if (beneAge === 15)
            ret = (state.plan.numberOfUnits * 465) * 0.026;
        if (beneAge === 16)
            ret = (state.plan.numberOfUnits * 465) * 0.022;
        if (beneAge >= 17)
            ret = (state.plan.numberOfUnits * 465) * 0.018;
    }

    return ret;
}

export const calculateDiscretionaryEAPAmount = (state) => {
    //Round half up

    let ret = 0.0;
    let EAP1 = 0.0;
    let EAP2 = 0.0;
    let EAP3 = 0.0;
    let EAPTopUpPercent1 = 0.0;
    let EAPTopUpPercent2 = 0.0;
    let EAPTopUpPercent3 = 0.0;
    let enrolmentFeeAmount = 0.0;

    if (state.plan.planTypeID === PLAN_TYPE_IDS.GROUP) {
        //Get the EAP values from the Plist config file
        EAP1 = getEAPConfigValue(state, "EAP1");
        EAP2 = getEAPConfigValue(state, "EAP2");
        EAP3 = getEAPConfigValue(state, "EAP3");
        //Get the EAP Top Up percentage values from the Plist config file
        EAPTopUpPercent1 = getEAPConfigValue(state, "TopUpPercent1");
        EAPTopUpPercent2 = getEAPConfigValue(state, "TopUpPercent2");
        EAPTopUpPercent3 = getEAPConfigValue(state, "TopUpPercent3");

        //Gets the enrolment fee amount for the current plan/mode
        enrolmentFeeAmount = getTotalEnrolmentFee(state);

        let e1 = EAP1 * EAPTopUpPercent1;
        e1 = roundHalfUp(e1);
        let e2 = EAP2 * EAPTopUpPercent2;
        e2 = roundHalfUp(e2);
        let e3 = EAP3 * EAPTopUpPercent3;
        e3 = roundHalfUp(e3);

        ret = state.plan.numberOfUnits * (e1 + e2 + e3);
    } else if (state.plan.planTypeID === PLAN_TYPE_IDS.ACHIEVERS) {
        ret = state.plan.numberOfUnits * getPlanTypesConfigValueForKey(planNameForPlanTypeIDUntranslated(state.plan.planTypeID), "TopUpPerUnit");
    }

    ret = roundHalfUp(ret);

    return ret;
}

export const calculateDiscretionaryReturn = (state, quoteAttributes) => {
    //Round half up to 0 decimals

    let ret = 0.0;

    if (state.plan.planTypeID === PLAN_TYPE_IDS.GROUP) {
        const enrolmentFeeAmount = getTotalEnrolmentFee(state);
        ret = enrolmentFeeAmount;
    } else if (state.plan.planTypeID === PLAN_TYPE_IDS.ACHIEVERS) {
        ret = quoteAttributes.enrolmentFee;
    }
    ret *= discretionarySalesChargeReturnRate(state);

    ret = roundHalfUp(ret, 0);
    return ret;
}


//Calcualtes the non-discretionary EAP amount based on EAP amounts, non-top up percent, and number of units.
export const calculateEAPAmount = (state, quoteAttributes) => {
    //Round half even

    let ret = 0.0;
    let EAP1 = 0.0;
    let EAP2 = 0.0;
    let EAP3 = 0.0;
    let EAPTopUpPercent1 = 0.0;
    let EAPTopUpPercent2 = 0.0;
    let EAPTopUpPercent3 = 0.0;

    if (state.plan.planTypeID === PLAN_TYPE_IDS.GROUP) {
        //Get the EAP values from the Plist config file
        EAP1 = getEAPConfigValue(state, "EAP1");
        EAP2 = getEAPConfigValue(state, "EAP2");
        EAP3 = getEAPConfigValue(state, "EAP3");
        //Get the EAP Top Up percentage values from the Plist config file
        EAPTopUpPercent1 = getEAPConfigValue(state, "TopUpPercent1");
        EAPTopUpPercent2 = getEAPConfigValue(state, "TopUpPercent2");
        EAPTopUpPercent3 = getEAPConfigValue(state, "TopUpPercent3");

        ret = quoteAttributes.numberOfUnits * ((1.0 - EAPTopUpPercent1) * EAP1 + (1.0 - EAPTopUpPercent2) * EAP2 + (1.0 - EAPTopUpPercent3) * EAP3);
    } else if ([PLAN_TYPE_IDS.ACHIEVERS, PLAN_TYPE_IDS.SI].includes(state.plan.planTypeID)) {
        ret = quoteAttributes.planDepositInterest;
    }

    ret = roundHalfUp(ret);
    return ret;
}


//If the total amount of the airmiles redemption is greater than allowed based on the plan and number of units, then drop the redemption amoutn down to the max
export const adjustAirMilesRedemption = (state, quoteAttributes) => {
    let airMilesRedemption = quoteAttributes.airMilesDollarsToRedeem;

    if (airMilesRedemption !== 0) {
        let numberOfUnits = state.plan.numberOfUnits;
        let maxAirMilesDollarsPerUnit = getDepositScheduleConfigValueForKeyWithState(state, "MaxAirMilesDollarsPerUnit");

        if (airMilesRedemption > maxAirMilesDollarsPerUnit * numberOfUnits) {
            let newDollars = (Math.trunc(maxAirMilesDollarsPerUnit * numberOfUnits)) - (Math.trunc(maxAirMilesDollarsPerUnit * numberOfUnits) % 100);
            quoteAttributes.airMilesDollarsToRedeem = newDollars;
        }
    }
}

//Based on the amount of the Air Miles redemption (some multiple of $100), determine how many deposits are covered
//And when the next deposit will be required.
export const projectAirMilesRedemptionCoverage = (state, quoteAttributes) => {
    let airMilesRedemptionDollarsRemaining = quoteAttributes.airMilesDollarsToRedeem;

    if (quoteAttributes.initialDepositAmount >= airMilesRedemptionDollarsRemaining) {
        airMilesRedemptionDollarsRemaining = 0;
    } else {
        let amount = quoteAttributes.initialDepositAmount;
        let incrementNextDepositDate = false;

        while (airMilesRedemptionDollarsRemaining > 0 && airMilesRedemptionDollarsRemaining >= amount) {
            airMilesRedemptionDollarsRemaining -= amount;
            if (incrementNextDepositDate) {
                quoteAttributes.nextDepositDate = determinedNextDepositDateAfterDate(state, quoteAttributes.nextDepositDate);
            }

            amount = quoteAttributes.amountPerDeposit;
            incrementNextDepositDate = true;
        }

        amount -= airMilesRedemptionDollarsRemaining;
        airMilesRedemptionDollarsRemaining = 0;
        quoteAttributes.amountRequiredOnNextDeposit = amount;
    }

    if (quoteAttributes.initialDepositAmount < quoteAttributes.airMilesDollarsToRedeem) {
        quoteAttributes.numberOfAdditionalDeposits -= Math.trunc(quoteAttributes.airMilesDollarsToRedeem - quoteAttributes.initialDepositAmount / quoteAttributes.amountPerDeposit)
        quoteAttributes.initialDepositAmount = quoteAttributes.airMilesDollarsToRedeem;
    }
}



//Will return the next deposit date after the provided date.
//If the next date is beyond the contribution lifetime, it will still return the date that would have been next.
//If there is onyl one deposit (T mode), returns the same date as was passed.
export const determinedNextDepositDateAfterDate = (state, date) => {

    let numberOfDepositsPerYear = getDepositModeConfigValueForKeyWithState(state, "NumberOfDepositsPerYear");
    let newDate = moment();

    if (numberOfDepositsPerYear === 12) {
        //Monthly
        //one month after the date provided, but on the same day as the effective date
        let newDate = moment(date).add(1, 'months').date(moment(state.plan.effectiveDate).day());
    } else if (numberOfDepositsPerYear === 1) {
        //Annual
        let newDate = moment(state.plan.effectiveDate);
        //TODO: This is the same as the iPad but I believe it is wrong and should use a loop like the next two
        if (newDate.isSameOrBefore(moment(date))) {
            newDate = moment(newDate).add(1, 'years');
        }
    } else if (numberOfDepositsPerYear === 2) {
        //Semi annual
        let newDate = moment(state.plan.effectiveDate);

        while (newDate.isSameOrBefore(date)) {
            newDate = newDate.add(6, 'months');
        }
    } else if (numberOfDepositsPerYear === 4) {
        // Quarterly
        let newDate = moment(state.plan.effectiveDate);

        while (newDate.isSameOrBefore(date)) {
            newDate = newDate.add(3, 'months');
        }
    } else {
        newDate = date;
    }

    return newDate;
}

export const getTotalGrantForQuote = (quoteAttributes) => {
    return quoteAttributes.CESGAmount + quoteAttributes.ACESGAmount + quoteAttributes.CLBAmount + quoteAttributes.QESIAmount + quoteAttributes.AQESIAmount + quoteAttributes.BCTESGAmount;
}
export const getTotalGrantInterestForQuote = (quoteAttributes) => {
    return quoteAttributes.CESGInterest + quoteAttributes.ACESGInterest + quoteAttributes.CLBInterest + quoteAttributes.QESIInterest + quoteAttributes.AQESIInterest + quoteAttributes.BCTESGInterest;
}

export const getNetSavingsForQuote = (quoteAttributes) => {
    const pda = roundHalfUp(quoteAttributes.planDepositAmount, 0);
    const af = roundHalfUp(quoteAttributes.adminFee, 0);
    const df = roundHalfUp(quoteAttributes.depositoryFee, 0);
    const ef = roundHalfUp(quoteAttributes.enrolmentFee, 0);
    const intDef = roundHalfUp(quoteAttributes.interestDeficiency, 0);

    const ret = pda - af - df - ef - intDef;

    return ret;
}